import React, { useEffect, useState } from "react";
import axios from "../../AxiosInstance";
import { Table, message } from "antd";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import Sidebar from "../../components/Sidebar";

const AdminUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    setIsLoading(true);
    try {
      const storedAuth = JSON.parse(localStorage.getItem("auth"));
      const token = storedAuth?.token;

      if (!token) {
        console.error("Token missing in localStorage");
        message.error("Authentication token missing");
        return;
      }

      const res = await axios.get("api/v1/auth/getAllUsers", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data && res.data.success) {
        const usersWithKeys = res.data.data.map((user, index) => ({
          ...user,
          key: user._id || index,
        }));

        // Sort by createdAt (newest first) - descending order
        const sorted = usersWithKeys.sort((a, b) => {
          const dateA = new Date(a.createdAt || a._id);
          const dateB = new Date(b.createdAt || b._id);
          return dateB - dateA; // Descending order (newest first)
        });

        setAllUsers(sorted);
        // Filter only admin users
        const admins = sorted.filter((user) => user.isAdmin === true);
        setAdminUsers(admins);
      }
    } catch (error) {
      console.error(
        "Error fetching users:",
        error.response?.data?.message || error.message
      );
      message.error("Failed to fetch admin users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!adminUsers || adminUsers.length === 0) {
      message.warning("No admin user data available");
      return;
    }

    const formattedData = adminUsers.map((user) => ({
      Name: user.name || "N/A",
      Email: user.email || "N/A",
      "Date Joined": user.createdAt
        ? new Date(user.createdAt).toLocaleDateString("en-GB")
        : "N/A",
      "Last Activity": user.latestActivity || "No activity yet",
      "Activity Time": user.activityTime
        ? new Date(user.activityTime).toLocaleString()
        : "N/A",
      "Is Ambassador": user.isAmbassador ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admin Users");
    XLSX.writeFile(workbook, "Admin_Users_List.xlsx");
  };

  const blockUser = async (userId) => {
    if (!window.confirm("Are you sure you want to block this admin user?")) {
      return;
    }

    try {
      const response = await axios.delete(`api/v1/admin/block-user/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success(response.data.message);
      // Refresh the list
      fetchAllUsers();
    } catch (error) {
      console.error(
        "Error blocking user:",
        error.response?.data?.message || error.message
      );
      toast.error(error.response?.data?.message || "Failed to block user");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Date Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-GB") : "N/A",
    },
    {
      title: "Last Activity",
      key: "latestActivity",
      render: (text, record) => (
        <div>
          <div className="font-medium">
            {record.latestActivity || "No activity yet"}
          </div>
          {record.activityTime && (
            <div className="text-xs text-gray-500">
              {new Date(record.activityTime).toLocaleString()}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Ambassador",
      dataIndex: "isAmbassador",
      key: "isAmbassador",
      render: (isAmbassador) => (
        <span className={isAmbassador ? "text-green-600" : "text-gray-500"}>
          {isAmbassador ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) => (
        <button
          className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
          onClick={() => blockUser(record._id)}
        >
          Block
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4 overflow-hidden">
        <h1 className="text-center mt-4 mb-4 text-2xl font-bold text-gray-800 flex-shrink-0">
          Admin Users
        </h1>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#34c3c9]"></div>
              <p className="mt-2 text-gray-600">Loading admin users...</p>
            </div>
          </div>
        ) : adminUsers.length > 0 ? (
          <>
            <div className="flex-1 overflow-auto min-h-0">
              <Table
                columns={columns}
                dataSource={adminUsers}
                pagination={{ pageSize: 10 }}
                scroll={{ x: "max-content", y: "calc(100vh - 250px)" }}
                rowKey="_id"
              />
            </div>
            <div className="flex justify-end mt-3 flex-shrink-0">
              <button
                className="px-6 py-2 text-white font-semibold rounded-lg bg-[#62C0C7] hover:bg-[#52a3a9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDownloadExcel}
                disabled={adminUsers.length === 0}
              >
                Download
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500 text-lg">No admin users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;

